export function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export interface VatPricingValues {
  basePrice: number;
  vatAmount: number;
  priceWithVat: number;
}

export function fromPriceWithVat(priceWithVat: number, vatPercentage: number): VatPricingValues {
  const normalizedPrice = roundMoney(Math.max(0, priceWithVat));
  if (normalizedPrice <= 0) {
    return { basePrice: 0, vatAmount: 0, priceWithVat: 0 };
  }
  if (vatPercentage <= 0) {
    return { basePrice: normalizedPrice, vatAmount: 0, priceWithVat: normalizedPrice };
  }
  const basePrice = roundMoney(normalizedPrice / (1 + vatPercentage / 100));
  const vatAmount = roundMoney(normalizedPrice - basePrice);
  return { basePrice, vatAmount, priceWithVat: normalizedPrice };
}

export function fromBasePrice(basePrice: number, vatPercentage: number): VatPricingValues {
  const normalizedBase = roundMoney(Math.max(0, basePrice));
  const vatAmount = roundMoney(normalizedBase * vatPercentage / 100);
  const priceWithVat = roundMoney(normalizedBase + vatAmount);
  return { basePrice: normalizedBase, vatAmount, priceWithVat };
}

export function fromVatAmount(basePrice: number, vatAmount: number): VatPricingValues {
  const normalizedBase = roundMoney(Math.max(0, basePrice));
  const normalizedVat = roundMoney(Math.max(0, vatAmount));
  const priceWithVat = roundMoney(normalizedBase + normalizedVat);
  return { basePrice: normalizedBase, vatAmount: normalizedVat, priceWithVat };
}

export function resolveVatPricing(
  basePrice: number,
  vatPercentage: number,
  vatAmount?: number | null,
): VatPricingValues {
  if (vatAmount != null && vatAmount >= 0 && basePrice > 0) {
    return fromVatAmount(basePrice, vatAmount);
  }
  return fromBasePrice(basePrice, vatPercentage);
}

export function getDisplayVatAmount(
  basePrice?: number | null,
  vatPercentage?: number | null,
  vatAmount?: number | null,
): number {
  if (vatAmount != null && vatAmount >= 0) {
    return vatAmount;
  }
  if (!basePrice) {
    return 0;
  }
  return roundMoney(basePrice * (vatPercentage || 0) / 100);
}

export function getDisplayPriceWithVat(
  basePrice?: number | null,
  vatPercentage?: number | null,
  vatAmount?: number | null,
): number {
  if (!basePrice) {
    return 0;
  }
  return roundMoney(basePrice + getDisplayVatAmount(basePrice, vatPercentage, vatAmount));
}
